import { Injectable, Injector, Inject } from '@angular/core';
import { HttpRequest, HttpResponse, HttpInterceptor, HttpHandler, HttpErrorResponse, HttpSentEvent, HttpHeaderResponse, HttpProgressEvent, HttpUserEvent } from '@angular/common/http';

import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { switchMap, catchError, finalize, filter, take } from 'rxjs/operators';

import { AUTH_SERVICE, AuthenticationService } from '../services';

import { AppConfig } from '@awp/core/config';

@Injectable()
export class AuthRequestInterceptor implements HttpInterceptor {

  isRefreshingToken = false;
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private _appConfig: AppConfig;
  get appConfig(): AppConfig {
    if (this._appConfig == null) {
      this._appConfig = this.injector.get(AppConfig);
    }
    return this._appConfig;
  }

  constructor(@Inject(AUTH_SERVICE) private authService: AuthenticationService, private injector: Injector) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any> | any> {
    if (request.url.startsWith('./assets')) {
      return next.handle(request);
    }

    if (!request.url.startsWith(this.appConfig.API_BASE_URL)
      || request.url.includes('/Application/Authenticate')
      || request.url.includes('/Application/ReAuthenticate')) {
      return next.handle(request);
    }

    return next.handle(this.addTokenToRequest(request, this.authService.token))
      .pipe(
        catchError(err => {
          if (err instanceof HttpErrorResponse) {
            switch ((<HttpErrorResponse>err).status) {           
              case 401:
                return this.handle401Error(request, next);
              case 400:
                return throwError(err);
              default:
                return throwError(err);
            }
          } else {
            return throwError(err);
          }
        })
      );
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {

    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.
      this.tokenSubject.next(null);

      return this.authService.refresh()
        .pipe(
          switchMap(entity => {
            if (entity != null) {
              this.tokenSubject.next(entity.properties.accessToken);
              return next.handle(this.addTokenToRequest(request, entity.properties.accessToken));
            }
          }),
          catchError(err => {
            return throwError(err);
          }),
          finalize(() => {
            this.isRefreshingToken = false;
          })
        );
    } else {
      return this.tokenSubject
        .pipe(
          filter(token => token != null),
          take(1),
          switchMap(token => {
            return next.handle(this.addTokenToRequest(request, token));
          })
        );
    }
  }
}
